import { ConsequenceNote, SkeletonSet } from '@cwos/ui-admin';
import { SettingsActions, SettingsCard } from '../settings/common.js';
import {
  ATTESTED_OVERRIDE_LABEL,
  ATTESTED_OVERRIDE_OFF_WARNING,
  ORGANIZATION_AUDIENCE,
  STRICTER_MODES,
  stricterModeNote,
} from './copy.js';
import { LocationSettingsShell } from './LocationSettingsShell.js';
import { ModeCards } from './ModeCards.js';
import { ProgramOverrides } from './ProgramOverrides.js';
import { PolicySchedule } from './PolicySchedule.js';
import { scheduleStatementOf, usePolicyForm } from './policyForm.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * General — the four policy modes as plain-language selection cards (directive
 * §3) plus the per-program override list (§11). Every control is the real
 * `location_policy` upsert; choosing a mode stricter than the saved one keeps
 * the previous page's publish-for-acknowledgement consequence, word for word.
 */
export function LocationGeneralPage() {
  const form = usePolicyForm(null);

  return (
    <LocationSettingsShell active="general" dirty={form.dirty}>
      <SettingsCard>
        <h2 className="rn-card-title">How location works on shifts</h2>
        {form.loading ? (
          <SkeletonSet surface="table" rows={4} />
        ) : form.loadError || !form.draft || !form.policy ? (
          <LoadFailure
            error={form.loadFailure}
            message="We couldn’t load the policy just now."
            onRetry={form.refetch}
          />
        ) : (
          <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-12)' }}>
            <ModeCards
              value={form.draft.mode}
              onChange={(mode) => form.set({ mode })}
              disabled={!form.canManage}
            />

            {form.draft.mode === 'REQUIRE_GEOFENCE' ? (
              <div style={{ display: 'grid', gap: 'var(--space-8)' }}>
                <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={form.draft.allowAttestedOverride}
                    disabled={!form.canManage}
                    onChange={(e) => form.set({ allowAttestedOverride: e.target.checked })}
                  />
                  <span>{ATTESTED_OVERRIDE_LABEL}</span>
                </label>
                {!form.draft.allowAttestedOverride ? (
                  <ConsequenceNote text={ATTESTED_OVERRIDE_OFF_WARNING} />
                ) : null}
              </div>
            ) : null}

            {/*
              THE NOTE BRANCHES BECAUSE THE ACT DOES. "As soon as you save" is
              exactly true of an unscheduled save and exactly false of a
              scheduled one; before this control existed only the first was
              reachable, which is why the constant could be a constant. A `null`
              statement means the operator is mid-choice and the screen says
              nothing rather than guessing which branch they are in.
            */}
            {STRICTER_MODES.has(form.draft.mode) &&
            form.draft.mode !== form.policy.mode &&
            scheduleStatementOf(form.draft) !== null ? (
              <ConsequenceNote
                text={stricterModeNote(scheduleStatementOf(form.draft)!, ORGANIZATION_AUDIENCE)}
              />
            ) : null}

            {/*
              ON EVERY PAGE THAT SAVES. This page sends the whole policy body,
              so it states a schedule on every save whether or not it shows one
              — and an omitted control would send `scheduledEffectiveAt: null`,
              clearing a schedule the operator set elsewhere.
            */}
            <PolicySchedule form={form} audience={ORGANIZATION_AUDIENCE} />

            {form.canManage ? (
              <SettingsActions
                dirty={form.dirty}
                valid={form.valid}
                saving={form.saving}
                saved={form.saved}
                error={form.saveError}
                onSave={form.save}
                onCancel={form.cancel}
                saveLabel="Save policy"
              />
            ) : null}
          </div>
        )}
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Per-program policies</h2>
        <div style={{ marginTop: 'var(--space-12)' }}>
          {form.policy ? (
            <ProgramOverrides orgPolicy={form.policy} canManage={form.canManage} />
          ) : form.loading ? (
            <SkeletonSet surface="table" rows={2} />
          ) : (
            <p className="rn-muted">
              Program policies appear once the organization policy has loaded.
            </p>
          )}
        </div>
      </SettingsCard>
    </LocationSettingsShell>
  );
}
